import {v4 as uuidv4} from 'uuid';
import {ApiCallback, ApiObject, ApiOptions, LoadOptions} from '@rudderstack/analytics-js';
import {getTrackingEndpoint} from '../utilities/getEndpoints';
import trackingCookie from '../utilities/trackingCookie';
import {
  IApiClientInput,
  IEventDictionary,
  ITrackedProperty,
  ITrackingTransformationInput,
} from '../types';
import graphqlTransformer from './graphqlTransformer';
import sendEvent from './sendEvent';
import validateEventAgainstDictionary from './validators/validateEventAgainstDictionary';
import validateEventDictionary from './validators/validateEventDictionary';
import Environment from '../constants/Environment';
import createRudderTracker from './createRudderTracker';

const createTracker = (
  eventDictionary: IEventDictionary,
  {
    apiUrl,
    trackingId,
    transformRequest = graphqlTransformer,
    ssr = false,
    validateAllowlist = true,
    providedEnvironment,
    rudderstackDataPlane,
    rudderstackDataPlaneUrl,
    rudderstackFFEnabled = false,
    rudderstackWriteKey,
    rudderstackConfig,
  }: {
    apiUrl?: string;
    trackingId?: string;
    validateAllowlist?: boolean;
    transformRequest?: (requestInformation: ITrackingTransformationInput) => IApiClientInput;
    ssr?: boolean;
    providedEnvironment?: Environment;
    rudderstackDataPlane?: string;
    rudderstackDataPlaneUrl?: string;
    rudderstackFFEnabled?: boolean;
    rudderstackWriteKey?: string;
    rudderstackConfig?: LoadOptions;
  } = {}
) => {
  validateEventDictionary(eventDictionary, {validateAllowlist});

  const usedApiUrl = apiUrl || getTrackingEndpoint(providedEnvironment);

  let usedTrackingId = trackingId || (ssr ? '' : trackingCookie.get());

  const usedRudderstackDataPlaneUrl = rudderstackDataPlaneUrl || rudderstackDataPlane;
  let rudderTracker: ReturnType<typeof createRudderTracker> | null = null;
  if (
    typeof window !== 'undefined' &&
    rudderstackFFEnabled &&
    usedRudderstackDataPlaneUrl &&
    rudderstackWriteKey
  ) {
    rudderTracker = createRudderTracker({
      dataPlaneUrl: usedRudderstackDataPlaneUrl,
      writeKey: rudderstackWriteKey,
      serviceName: eventDictionary.namespace,
      loadOptions: rudderstackConfig,
    });
  }

  return {
    setTrackingId: (newTrackingId: string): void => {
      usedTrackingId = newTrackingId;
    },

    track: (eventName: string, properties?: ITrackedProperty): Promise<boolean> => {
      validateEventAgainstDictionary({
        eventDictionary,
        eventName,
        properties,
      });

      const transformerProperties = {
        apiUrl: usedApiUrl,
        eventName,
        namespace: eventDictionary.namespace,
        properties: {
          ...properties,
          url: window?.location?.href,
          unique_record_id: uuidv4(),
        },
        trackingId: usedTrackingId,
      };

      if (!usedTrackingId) {
        return trackingCookie.set(providedEnvironment).then((cookieId: string) => {
          transformerProperties.trackingId = cookieId;
          usedTrackingId = cookieId;

          rudderTracker?.trackFromLegacy(eventName, transformerProperties);

          return sendEvent(transformRequest(transformerProperties), {ssr});
        });
      }

      rudderTracker?.trackFromLegacy(eventName, transformerProperties);
      return sendEvent(transformRequest(transformerProperties), {ssr});
    },

    trackPageView: (
      category: string,
      pageName: string,
      pageProperties?: ApiObject,
      apiOptions?: ApiOptions
    ): boolean => {
      if (!rudderTracker) return false;

      rudderTracker.trackPage(category, pageName, pageProperties, apiOptions);
      return true;
    },

    trackIdentify: (
      userId: string,
      traits?: ApiObject,
      apiOptions?: ApiOptions,
      callback?: ApiCallback
    ): boolean => {
      if (!rudderTracker) return false;

      rudderTracker.trackIdentify(userId, traits, apiOptions, callback);
      return true;
    },

    rudderTrack: (
      eventName: string,
      properties?: ApiObject,
      apiOptions?: ApiOptions,
      callback?: ApiCallback
    ): boolean => {
      if (!rudderTracker) return false;

      rudderTracker.track(eventName, properties, apiOptions, callback);
      return true;
    },

    rudderTracker: () => {
      return rudderTracker;
    },
  };
};

export default createTracker;
